import {
  App,
  Button as ButtonAnt,
  Col,
  Divider,
  Form,
  FormProps,
  Input,
  Popconfirm,
  Row,
  Space,
  Tooltip,
  Typography
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { css } from '@emotion/react';
import { Button } from '../button';
import { UnitConversionKey } from '@/enums/unit-conversion-key.enum';
import { useCallback, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  getUnitConversion,
  resetMessages,
  updateUnitConversion,
  watchUnitConversionList,
  watchUnitConversionMessage
} from '@/redux/slices/unit-conversion.slice';
import { setMessages } from '@/redux/slices/app.slice';
import usePermission from '@/hooks/permission.hook';
import { useBeforeUnload } from 'react-router-dom';

type FieldType = {
  id: number;
  unit: string;
  conversionRate: string;
  isEditable: boolean;
  [key: string]: any;
};

type UnitConversionFormProps = {
  selectedKey: string;
};

export const UnitConversionForm = ({
  selectedKey = UnitConversionKey.BloodSugarFasting
}: UnitConversionFormProps) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const unitConversionList = useAppSelector(watchUnitConversionList);
  const messageInfo = useAppSelector(watchUnitConversionMessage);
  const { modal, notification } = App.useApp();

  const { creatable, updatable, deletable } = usePermission();

  const [units, setUnits] = useState<FieldType[]>([]);
  const [nextId, setNextId] = useState(2);
  const [model, setModel] = useState('HOSPITAL');
  const [unit, setUnit] = useState('');

  useBeforeUnload((e) => {
    const isDirty =
      unitConversionList.length !== units.length ||
      units.some((unit) => unit.isEditable);

    if (isDirty) {
      e.preventDefault();
    }
  });

  const fetchData = useCallback(() => {
    switch (selectedKey) {
      case UnitConversionKey.BloodSugarFasting:
        setModel('HUNGRY');
        dispatch(getUnitConversion('HUNGRY'));
        setUnit('mmol/L');
        break;
      case UnitConversionKey.BloodSugar2H:
        setModel('2_HOURS');
        dispatch(getUnitConversion('2_HOURS'));
        setUnit('mmol/L');
        break;
      case UnitConversionKey.BloodSugarHbA1c:
        setModel('HBA1C');
        dispatch(getUnitConversion('HBA1C'));
        setUnit('%');
        break;
      case UnitConversionKey.LiverEnzymeSgot:
        setModel('SGOT');
        dispatch(getUnitConversion('SGOT'));
        setUnit('U/L');
        break;
      case UnitConversionKey.LiverEnzymeSgpt:
        setModel('SGPT');
        dispatch(getUnitConversion('SGPT'));
        setUnit('U/L');
        break;
      case UnitConversionKey.LipidCholesterol:
        setModel('CHOL');
        dispatch(getUnitConversion('CHOL'));
        setUnit('mmol/L');
        break;
      case UnitConversionKey.LipidLdl:
        setModel('LDL');
        dispatch(getUnitConversion('LDL'));
        setUnit('mmol/L');
        break;
      case UnitConversionKey.LipidHdl:
        setModel('HDL');
        dispatch(getUnitConversion('HDL'));
        setUnit('mmol/L');
        break;
      case UnitConversionKey.LipidTriglyceride:
        setModel('TRI');
        dispatch(getUnitConversion('TRI'));
        setUnit('mmol/L');
        break;
      case UnitConversionKey.Urea:
        setModel('URE');
        dispatch(getUnitConversion('URE'));
        setUnit('mmol/L');
        break;
      case UnitConversionKey.Creatinine:
        setModel('CREA');
        dispatch(getUnitConversion('CREA'));
        setUnit('µmol/L');
        break;
      case UnitConversionKey.UricAcid:
        setModel('AXIT_URIC');
        dispatch(getUnitConversion('AXIT_URIC'));
        setUnit('µmol/L');
        break;
      default:
        return 'Default';
    }
  }, [selectedKey, dispatch]);

  const handleAddUnit = () => {
    const currentValues = form.getFieldsValue();

    setUnits((prev) => [
      ...prev.map((item) => ({
        ...item,
        unit: currentValues[`unit_${item.id}`],
        conversionRate: currentValues[`conversionRate_${item.id}`]
      })),
      { id: nextId + 1, unit: '', conversionRate: '', isEditable: true }
    ]);

    setNextId(nextId + 1);
  };

  const handleEdit = (id: number) => {
    setUnits(
      units.map((unit) =>
        unit.id === id ? { ...unit, isEditable: true } : unit
      )
    );
  };

  const confirmEditUnit = (id: number) => {
    modal.confirm({
      title: 'Lưu ý khi cập nhật tỉ lệ chuyển đổi',
      content: (
        <em>
          Các dữ liệu của người dùng đã nhập trước đây theo tỉ lệ chuyển đổi cũ
          sẽ không được cập nhật theo tỉ lệ chuyển đổi mới
        </em>
      ),
      okText: 'Xác nhận',
      width: 450,
      cancelText: 'Hủy',
      onOk: () => handleEdit(id)
    });
  };

  const handleDelete = (id: number) => {
    setUnits(units.filter((unit) => unit.id !== id));
  };

  const isUnitConversionDuplicated = (arr: string[]) => {
    for (let i = 0; i < arr.length - 1; i++) {
      for (let j = i + 1; j < arr.length; j++) {
        if (arr[i] === arr[j]) {
          return true;
        }
      }
    }
    return false;
  };

  const onFinish: FormProps<FieldType>['onFinish'] = async (values) => {
    const unitList = units.map((unit) => values[`unit_${unit.id}`]);

    if (isUnitConversionDuplicated(unitList)) {
      notification.error({ message: 'Tên đơn vị này đã tồn tại.' });
      return;
    }

    const unitsData: Record<string, any>[] = units.map((unit) => {
      return {
        model,
        indicator: values[`unit_${unit.id}`],
        value: values[`conversionRate_${unit.id}`]
      };
    });

    await dispatch(
      updateUnitConversion({ model, items: JSON.stringify(unitsData) })
    );
    fetchData();
  };
  const confirmUpdate = () => {
    modal.confirm({
      title: 'Lưu ý khi cập nhật tỉ lệ chuyển đổi',
      content: (
        <em>
          Các dữ liệu của người dùng đã nhập trước đây theo tỉ lệ chuyển đổi cũ
          sẽ không được cập nhật theo tỉ lệ chuyển đổi mới
        </em>
      ),
      okText: 'Ok đã hiểu',
      width: 450,
      cancelButtonProps: { style: { display: 'none' } },
      onOk: async () => {
        const values = form.getFieldsValue();
        await onFinish(values);
      }
    });
  };
  const confirmDelete = (id: number) => {
    modal.confirm({
      title: 'Bạn có chắc chắn xóa đơn vị này không',
      content: <em>Người dùng sẽ không thấy đơn vị này hiển thị nữa</em>,
      okText: 'Xóa',
      cancelText: 'Hủy',
      width: 450,
      onOk: async () => {
        await handleDelete(id);
      }
    });
  };
  const handleCancel = () => {
    fetchData();
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (unitConversionList) {
      setUnits(
        unitConversionList.map((unit) => ({
          id: unit.id,
          unit: unit.indicator,
          conversionRate: String(unit.value),
          isEditable: false
        }))
      );
    }
  }, [unitConversionList]);

  useEffect(() => {
    const initialValues = units.reduce(
      (acc, unit) => {
        acc[`unit_${unit.id}`] = unit.unit;
        acc[`conversionRate_${unit.id}`] = unit.conversionRate;
        return acc;
      },
      {} as Record<string, string>
    );

    form.setFieldsValue(initialValues);
  }, [form, units]);

  useEffect(() => {
    if (messageInfo) {
      dispatch(
        setMessages([
          {
            type: messageInfo.type,
            message: messageInfo.message,
            description: messageInfo.description
          }
        ])
      );

      dispatch(resetMessages());
    }
  }, [dispatch, messageInfo]);

  return (
    <div css={contentAreaStyles}>
      <Row>
        <Col>
          <Typography.Text>
            Đơn vị tiêu chuẩn của chỉ số này tại PMS là <strong>{unit}</strong>
            &nbsp;
            <Popconfirm
              placement='bottomLeft'
              title='Hướng dẫn tạo đơn vị đo lường ở chỉ số sức khoẻ'
              description={
                <div css={healthUnitsDesc}>
                  <ul>
                    <li>Bước 1: Nhấn nút Thêm mới đơn vị</li>
                    <li>
                      Bước 2: Nhập tên đơn vị ở trường thông tin “Nhập tên đơn
                      vị mới”. Ví dụ: nhập mg/dL
                    </li>
                    <li>
                      Bước 3: Nhập tỉ lệ chuyển đổi (tỉ lệ chuyển đổi này có
                      nghĩa: 1 đơn vị mới bằng bao nhiêu đơn vị tiêu chuẩn) Ví
                      dụ nhập 18
                    </li>
                    <li>Bước 4: Nhấn lưu. Lưu thành công</li>
                  </ul>
                  <p>
                    Ví dụ ở phía người dùng: khi người dùng nhập 5 mg/dL và nhấn
                    Lưu, hệ thống sẽ tự động chuyển đổi về đơn vị tiêu chuẩn của
                    hệ thống là 90 mmol/L và lưu giá trị 90 mmol/L này lại ở Cơ
                    sở dữ liệu, cũng như hiển thị ở Lịch sử cập nhật chỉ số,
                    hiển thị giá trị này trên biểu đồ
                  </p>
                </div>
              }
              okText='Đã hiểu'
              showCancel={false}
              icon={<InfoCircleOutlined css={infoIconStyle} />}
            >
              <ButtonAnt
                shape='circle'
                type='text'
                icon={<InfoCircleOutlined css={infoIconStyle} />}
              />
            </Popconfirm>
          </Typography.Text>
        </Col>
      </Row>
      <Form
        form={form}
        onFinish={confirmUpdate}
        css={formStyles}
        layout='vertical'
      >
        {units.map((unit, index) => (
          <div key={unit.id}>
            <Row align='middle' gutter={16}>
              <Col flex={1}>
                <Form.Item<FieldType>
                  name={`unit_${unit.id}`}
                  rules={[
                    { required: true, message: 'Vui lòng nhập tên đơn vị!' }
                  ]}
                >
                  <Input
                    allowClear
                    prefix={1}
                    value={unit.unit}
                    placeholder='Nhập tên đơn vị mới'
                    disabled={!unit.isEditable}
                  />
                </Form.Item>
              </Col>
              <Col>=</Col>
              <Col flex={1}>
                <Form.Item<FieldType>
                  name={`conversionRate_${unit.id}`}
                  rules={[
                    {
                      required: unit.isEditable,
                      message: 'Vui lòng nhập tỷ lệ chuyển đổi!'
                    },
                    {
                      pattern: /^(?!0+(?:[.,]0+)?$)\d+([.,]?\d+)?$/,
                      message: 'Tỉ lệ chuyển đổi phải là số dương, lớn hơn 0'
                    }
                  ]}
                  normalize={(value) => value && value.replace(/,/g, '.')}
                >
                  <Input
                    css={inputStyles}
                    placeholder='Nhập tỉ lệ chuyển đổi'
                    disabled={!unit.isEditable}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[1-9]\d*$/.test(value) || value === '') {
                        form.setFieldValue(`conversionRate_${unit.id}`, value);
                      }
                    }}
                  />
                </Form.Item>
              </Col>
              <Col>
                <Space>
                  {!unit.isEditable && updatable && (
                    <Tooltip title='Cập nhật'>
                      <ButtonAnt
                        icon={<EditOutlined />}
                        type='primary'
                        onClick={() => confirmEditUnit(unit.id)}
                      />
                    </Tooltip>
                  )}
                  {deletable && (
                    <Tooltip title='Xoá'>
                      <ButtonAnt
                        icon={<DeleteOutlined />}
                        type='primary'
                        danger
                        onClick={() => confirmDelete(unit.id)}
                      />
                    </Tooltip>
                  )}
                  {!(!unit.isEditable && unit.unit && unit.conversionRate) && (
                    <span css={unitPlaceholderStyles} />
                  )}
                </Space>
              </Col>
            </Row>
            {index < units.length - 1 && <Divider css={dividerStyles} />}
          </div>
        ))}

        {creatable && (
          <Button
            icon={<PlusOutlined />}
            onClick={handleAddUnit}
            css={addUnitBtnStyles(units.length)}
          >
            Thêm mới đơn vị
          </Button>
        )}
        {(unitConversionList.length > 0 || units.length > 0) &&
          (updatable || deletable) && (
            <Row justify='end' css={footerButtonGroupStyles}>
              <Col>
                <Space>
                  <Button shape='round' onClick={handleCancel}>
                    Huỷ
                  </Button>
                  <Button htmlType='submit' type='primary' shape='round'>
                    Lưu
                  </Button>
                </Space>
              </Col>
            </Row>
          )}
      </Form>
    </div>
  );
};

const contentAreaStyles = css`
  margin-left: 1.4rem;
  background: var(--white-color);
  padding: 2rem;
  border-radius: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

const formStyles = css`
  max-width: 80rem;
  .ant-form-item {
    margin-bottom: 0;
  }

  .ant-form-item .ant-form-item-explain-error {
    position: absolute;
  }
`;

const infoIconStyle = css`
  color: var(--success-color) !important;
`;

const healthUnitsDesc = css`
  max-width: 40rem;
  color: var(--black-50-opacity);
  font-size: 1.4rem;
  ul {
    margin-left: 2.6rem;
    margin-bottom: 1rem;
    li {
      list-style: disc;
    }
  }
  p {
    margin-bottom: 0;
  }
`;

const addUnitBtnStyles = (length: number) => css`
  margin-top: ${length > 0 ? '2rem' : '0'};
  &:hover {
    background: none !important;
    color: var(--primary-color) !important;
    opacity: 0.85;
  }
`;

const dividerStyles = css`
  margin: 2.4rem 0;
`;

const footerButtonGroupStyles = css`
  margin-top: 2.4rem;
  button {
    width: 8rem;
  }
`;

const unitPlaceholderStyles = css`
  display: inline-block;
  width: 3.2rem;
`;

const inputStyles = css`
  width: 100%;
`;
